// src/components/hospitalizacion/NutricionCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  CircularProgress,
  Stack
} from '@mui/material';
import { Restaurant } from '@mui/icons-material';
import api from '../../api/api';
import dayjs from 'dayjs';

const NutricionCard = ({ hospitalizacionId }) => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hospitalizacionId) {
      fetchNutricion();
    }
  }, [hospitalizacionId]);

  const fetchNutricion = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await api.get(`/nutricion-hospitalaria/${hospitalizacionId}?limit=10`);
      setRegistros(res.data);
    } catch (err) {
      console.error('Error cargando nutrición:', err);
      setError('No se pudieron cargar los registros de alimentación');
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  };

  // Cálculo de promedio de consumo
  const promedioConsumo = registros.length > 0
    ? Math.round(registros.reduce((acc, r) => acc + (r.consumo_porcentaje || 0), 0) / registros.length)
    : 0;

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
      <Box sx={{ p: 2, bgcolor: '#10b981', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Restaurant sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            ALIMENTACIÓN / NUTRICIÓN
          </Typography>
        </Box>

        <Chip
          label={`Consumo promedio: ${promedioConsumo}%`}
          sx={{ bgcolor: 'white', color: '#10b981', fontWeight: 'bold' }}
        />
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell>HORA</TableCell>
              <TableCell>ALIMENTO</TableCell>
              <TableCell align="center">CANTIDAD OFRECIDA</TableCell>
              <TableCell align="center">CONSUMO</TableCell>
              <TableCell>VÍA / TIPO</TableCell>
              <TableCell>RESPONSABLE</TableCell>
              <TableCell>RECHAZO / NOTAS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {registros.map((r) => (
              <TableRow key={r.id_registro} hover>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {dayjs(r.fecha_hora).format('hh:mm A')}
                </TableCell>

                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {r.alimento || '—'}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  {r.cantidad_ofrecida || '—'}
                </TableCell>

                <TableCell align="center">
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={r.consumo_porcentaje || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: r.consumo_porcentaje >= 70 ? '#10b981' : r.consumo_porcentaje >= 30 ? '#f59e0b' : '#ef4444'
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    {r.consumo_porcentaje || 0}%
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={r.via_administracion?.toUpperCase() || '—'}
                    size="small"
                    color="info"
                  />
                </TableCell>

                <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
           <Box sx={{ 
             width: 24, height: 24, bgcolor: '#10b981', color: 'white', 
             borderRadius: '50%', fontSize: '0.65rem', display: 'flex', 
             alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
           }}>
             {r.usuario_nombre ? r.usuario_nombre.substring(0, 2).toUpperCase() : '??'}
           </Box>
           <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
             {r.usuario_nombre || 'Sistema'}
           </Typography>
        </Stack>
      </TableCell>




                <TableCell sx={{ fontSize: '0.78rem' }}>
                  {r.rechazo || r.notas || '—'}
                </TableCell>
              </TableRow>
            ))}

            {registros.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aún no hay registros de alimentación
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Resumen al pie (opcional) */}
      <Box sx={{ p: 2, bgcolor: '#f8fafc', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Promedio de consumo: <strong>{promedioConsumo}%</strong> (últimos registros)
        </Typography>
      </Box>
    </Card>
  );
};

export default NutricionCard;